import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { mediaUrl } from '../../config/api';
import { fieldKey } from '../../fieldKeys';
import { asTags } from '../../fieldTypes';
import { hasSources, presetSources, selectLabel, useKinds, uiLang, usedFields, useVocabulary, vocabLabels } from '../../kinds';
import { SOURCE_SCHEMES, sourceLabel, useSources } from '../../sources';
import { placeName } from '../../locations';
import {
  Badge, Breadcrumb, Button, EmptyState, IconButton, Menu, Skeleton, useToast,
} from '../../ui';
import {
  ChevronLeftIcon, ChevronRightIcon, EditIcon, LinkIcon, MoreIcon, PlusIcon, RefreshIcon, SearchIcon, TrashIcon,
} from '../../ui/icons';
import FieldValue, { FieldDisplay, formatDate } from '../FieldValue';
import { ItemCover } from '../collection/ItemViews';
import { itemSummary } from '../collection/itemSummary';
import { refreshError } from '../ItemForm';
import { tagKey } from '../collection/fold';
import { SERIALIZE, filtersFromParams, hasFilters, queryParams } from '../collection/filters';
import Lightbox from './Lightbox';
import { codeOfItem, useFindDetails } from './findDetails';
import MyOpinion from './MyOpinion';
import { relinkError, relinkWarnings, useRelink, warningText } from './relink';
import { igdbPage, rawName, sourceIds, sourceOf, sourceUrl, tmdbKindOf } from './sources';

const LONG_LIST = 8;          // a hidden tags field past this gets its own section
const LONG_LIST_SHOWN = 30;
const CHIP_TYPES = ['vocab', 'tags', 'checkbox'];

/**
 * The list filtered on one value alone (phase 3 §3.5): a chip or a name of
 * the item page is a new question, so the page's own context (search, other
 * filters) is left behind. Tags as the server keys them, codes as stored.
 */
export const filterPath = (basePath, name, value) => `${basePath}?${new URLSearchParams([[name, value]]).toString()}`;
const valueParam = (field, value) => (field.type === 'tags' ? tagKey(value) : String(value));

const isEmpty = (value) => value === null || value === undefined || value === ''
  || (Array.isArray(value) && value.length === 0)
  || (value && typeof value === 'object' && !Array.isArray(value)
    && ('amount' in value ? value.amount : 'value' in value ? value.value : 1) === '');

/** The values of a chips field (vocabulary codes, options, tags), each a link to the list filtered on it. */
function ValueChips({ field, value, basePath }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const vocabulary = useVocabulary(field.type === 'vocab' ? field.vocabulary : null);
  const raw = field.type === 'tags' ? asTags(value) : (Array.isArray(value) ? value : [value]).filter((v) => v !== null && v !== '');
  const labels = field.type === 'vocab'
    ? vocabLabels(vocabulary, raw, lang, field)
    : field.type === 'checkbox'
      ? raw.map((v) => String(selectLabel(field, v, lang)))
      : raw;
  return labels.map((label, i) => (
    <li key={`${field.key}-${i}`}>
      <Link className="item-page__chip" to={filterPath(basePath, `f.${fieldKey(field)}`, valueParam(field, raw[i]))}
            title={t('item.filterBy', { name: field.name, value: label })}>
        {label}
      </Link>
    </li>
  ));
}

/** A hidden tags field with many values (the cast): the first 30, then all. */
function LongList({ field, value }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const tags = asTags(value);
  const shown = all ? tags : tags.slice(0, LONG_LIST_SHOWN);
  const id = `item-list-${field.key}`;
  return (
    <section aria-labelledby={id} className="item-page__section">
      <h2 id={id} className="item-page__h2">{field.name}</h2>
      <ul className="item-page__chips">
        {shown.map((tag, i) => <li key={i} className="item-page__chip">{tag}</li>)}
      </ul>
      {tags.length > LONG_LIST_SHOWN && (
        <Button variant="ghost" size="sm" onClick={() => setAll(!all)} aria-expanded={all}>
          {all ? t('item.showFewer') : t('item.showAll', { count: tags.length })}
        </Button>
      )}
    </section>
  );
}

/** The description, cut at five lines with "Read more" when it is longer. */
function Description({ text }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  useLayoutEffect(() => {
    const node = ref.current;
    if (node && !open) setLong(node.scrollHeight > node.clientHeight + 1);
  }, [text, open]);
  return (
    <div className="item-page__description">
      <p ref={ref} className={open ? '' : 'is-clamped'}>{text}</p>
      {(long || open) && (
        <Button variant="ghost" size="sm" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? t('item.readLess') : t('item.readMore')}
        </Button>
      )}
    </div>
  );
}

/** The first YYYY-MM-DD of a server timestamp, long ("2 août 2025"). */
const dayOf = (timestamp, lang) => (timestamp ? formatDate(String(timestamp).slice(0, 10), lang) : '');
const changedLater = (item) => {
  const created = Date.parse(String(item.created_at || '').replace(' ', 'T'));
  const updated = Date.parse(String(item.updated_at || '').replace(' ', 'T'));
  return Number.isFinite(created) && Number.isFinite(updated) && updated - created > 60 * 1000;
};


/**
 * /c/:id/items/:itemId — the read view of one item, for everyone who can
 * read the collection (spec §1). Its query string is the list it was
 * opened from (q, type, sort, dir): the server says where the item stands in
 * it (neighbours), for ‹ n of N › and ← →, which replace the address so Back
 * returns to the list.
 */
function ItemPage({
  inventory, itemId, itemTypes, icon: Icon, canEdit, basePath, context, perPage, onDelete, onChanged, onOpinionSaved,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'en';
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [item, setItem] = useState(null);
  const [missing, setMissing] = useState(false);
  const [neighbours, setNeighbours] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // A cover that does not load (a file missing) shows the placeholder, as on cards.
  const [coverFailed, setCoverFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const kinds = useKinds();   // which sources fill each preset (GET /kinds)
  const contextString = context.toString();

  // The item and where it stands, in parallel.
  useEffect(() => {
    let alive = true;
    setMissing(false);
    api.get(`/inventories/${inventory.id}/items/${itemId}`)
      .then((response) => { if (alive) setItem(response.data); })
      .catch(() => { if (alive) { setItem(null); setMissing(true); } });
    return () => { alive = false; };
  }, [inventory.id, itemId, reload]);
  useEffect(() => {
    let alive = true;
    setNeighbours(null);
    // The list's own parameters, repeated ones (f.genres=a&f.genres=b, place=…) kept; not its page.
    const context = new URLSearchParams(contextString);
    context.delete('page');
    api.get(`/inventories/${inventory.id}/items/${itemId}/neighbors`,
      { params: { ...queryParams(context), per_page: perPage }, paramsSerializer: SERIALIZE })
      .then((response) => { if (alive) setNeighbours(response.data); })
      .catch(() => { if (alive) setNeighbours(null); });
    return () => { alive = false; };
  }, [inventory.id, itemId, contextString, perPage]);
  useEffect(() => { window.scrollTo(0, 0); }, [itemId]);

  const itemPath = useCallback((id) => `${basePath}/items/${id}${contextString ? `?${contextString}` : ''}`, [basePath, contextString]);
  const go = useCallback((id) => {
    if (id) navigate(itemPath(id), { replace: true, state: location.state });
  }, [navigate, itemPath, location.state]);

  // ← and → go to the previous and next item, outside text fields and other widgets using arrows.
  useEffect(() => {
    const onKey = (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (document.body.classList.contains('has-modal')) return;
      const target = event.target;
      if (target && target.closest && target.closest('input, textarea, select, [contenteditable="true"], [role="radiogroup"], [role="menu"], [role="tablist"], [role="slider"]')) return;
      if (!neighbours) return;
      const id = event.key === 'ArrowLeft' ? neighbours.previous_id : neighbours.next_id;
      if (id) {
        event.preventDefault();
        go(id);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [neighbours, go]);

  const type = (item && item.item_type_id && itemTypes.find((candidate) => candidate.id === item.item_type_id))
    || inventory.default_item_type || itemTypes[0] || null;
  const fields = useMemo(() => usedFields((type && type.fields) || inventory.fields || []), [type, inventory.fields]);

  // The list's address: its context, on the page holding this item.
  const listQuery = (withContext) => {
    const params = new URLSearchParams(withContext ? contextString : '');
    if (!withContext) {
      ['sort', 'dir', 'group'].forEach((key) => { const v = context.get(key); if (v) params.set(key, v); });
    }
    params.delete('page');
    if (neighbours && neighbours.page > 1) params.set('page', String(neighbours.page));
    const query = params.toString();
    return `${basePath}${query ? `?${query}` : ''}`;
  };
  const filtered = !!(context.get('q') || context.get('type') || hasFilters(filtersFromParams(context)));

  // An item of a model with sources but no source id, holding an ISBN or an EAN: "Find details" looks it up.
  const itemCode = item ? codeOfItem(fields, item.field_values) : null;
  const refreshKeysOf = (preset) => (presetSources(preset, kinds).includes('tmdb') ? 'itemForm.refresh' : 'itemForm.refreshSources');
  const findDetails = useFindDetails({
    inventoryId: inventory.id, itemId: item && item.id, itemType: type, code: itemCode, lang: uiLang(i18n),
    onDone: (answer) => {
      const changed = (answer.changed || []).map((key) => (fields.find((f) => f.key === key) || {}).name || key);
      const keys = refreshKeysOf(type && type.preset);
      toast[changed.length ? 'success' : 'info'](changed.length ? t(`${keys}.changed`, { fields: changed.join(', ') }) : t(`${keys}.nothing`));
      setReload((n) => n + 1);
      if (onChanged) onChanged();
    },
    onError: (error) => toast.error(refreshError(t, error)),
  });
  // "Not this film…": the item linked to the right entry (what came from the wrong one replaced, the copy kept).
  const sources = useSources();
  const relink = useRelink({
    inventoryId: inventory.id, item, itemType: type, fields, lang: uiLang(i18n),
    title: item ? itemSummary(item, fields).title : '',
    sourceName: (id) => sourceLabel(id, sources, uiLang(i18n)),
    onDone: (answer, candidate) => {
      toast.success(t('relink.done', { title: candidate.title }));
      if ((answer.duplicates || []).length > 0) toast.info(t('relink.alsoHere', { count: answer.duplicates.length }));
      // What the source could not give (a poster not downloaded, a source that did not answer…), said once each.
      const name = (key) => (fields.find((f) => f.key === key) || {}).name || key;
      relinkWarnings(answer.warnings).filter((w) => ['source_skipped', 'image_not_stored'].includes(w.code)).forEach((warning) => toast.info(warningText(t, warning, name, (id) => sourceLabel(id, sources, uiLang(i18n)))));
      setReload((n) => n + 1);
      if (onChanged) onChanged();
    },
    onError: (error) => {
      toast.error(relinkError(t, error));
      // Changed meanwhile: shown again as it is now, to relink from there.
      if (error?.response?.data?.code === 'item_changed') setReload((n) => n + 1);
    },
  });

  const crumbs = (title) => [
    { label: inventory.name, to: filtered ? basePath : listQuery(false) },
    ...(filtered ? [{
      label: context.get('q') ? t('item.searchCrumb', { query: context.get('q') }) : t('item.filteredCrumb'),
      to: listQuery(true),
    }] : []),
    { label: title },
  ];

  if (missing) {
    return (
      <div className="item-page">
        <Breadcrumb items={[{ label: inventory.name, to: basePath }, { label: t('item.missingCrumb') }]} />
        <EmptyState icon={SearchIcon} title={t('item.missing')} titleAs="h1"
                    action={<Button as={Link} variant="secondary" to={listQuery(true)}>{t('item.backToList')}</Button>}>
          {t('item.missingText')}
        </EmptyState>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="item-page" aria-busy="true">
        <Skeleton height={14} width="40%" />
        <div className="item-page__body">
          <div className="item-page__media"><Skeleton className="item-page__cover-skeleton" height="auto" radius="var(--radius-lg)" /></div>
          <div className="item-page__main">
            <Skeleton height={40} width="70%" />
            <Skeleton height={16} width="45%" />
            <Skeleton height={88} radius="var(--radius-lg)" />
          </div>
        </div>
      </div>
    );
  }

  const values = item.field_values || {};
  const summary = itemSummary(item, fields);
  const title = summary.title || t('collection.untitled');
  const has = (field) => !isEmpty(values[fieldKey(field)]);
  const roleField = (role) => fields.find((field) => field.role === role);
  const shown = new Set();
  const take = (field) => { if (field) shown.add(fieldKey(field)); return field; };
  ['title', 'subtitle', 'year', 'creators', 'description'].forEach((role) => take(roleField(role)));

  // Header: the type (several types), the first in-view select used (the format: "DVD").
  const badgeField = fields.find((field) => field.type === 'select' && field.in_view !== false && has(field));
  const measureField = take(fields.find((field) => field.type === 'measure' && field.in_view !== false && has(field)));
  const creatorsField = roleField('creators');
  const subtitleField = roleField('subtitle');
  const descriptionField = roleField('description');

  // Images: the cover first, then every image field's photos in field order.
  const imageFields = fields.filter((field) => field.type === 'image');
  imageFields.forEach(take);
  const images = [];
  const seen = new Set();
  const addImage = (id, caption) => {
    if (Number.isInteger(id) && !seen.has(id)) { seen.add(id); images.push({ id, caption }); }
  };
  const coverField = imageFields.find((field) => field.role === 'cover') || imageFields[0];
  if (item.cover_media_id) addImage(item.cover_media_id, coverField ? coverField.name : '');
  imageFields.forEach((field) => (Array.isArray(values[fieldKey(field)]) ? values[fieldKey(field)] : []).forEach((id) => addImage(id, field.name)));

  const chipFields = fields.filter((field) => CHIP_TYPES.includes(field.type) && field.in_view !== false
    && field !== creatorsField && has(field)).map(take);
  const longLists = fields.filter((field) => field.type === 'tags' && field.in_view === false
    && asTags(values[fieldKey(field)]).length > LONG_LIST).map(take);
  const details = fields.filter((field) => !shown.has(fieldKey(field)) && has(field));
  const place = item.location_path && item.location_path.length > 0 ? item.location_path : null;

  const preset = type && type.preset;
  const tmdbKind = tmdbKindOf(type);
  const ids = sourceIds(fields, values);
  const { tmdb: tmdbId, imdb: imdbId } = ids;
  // The book sources' pages (Open Library, the BnF, Google Books), in the model's order.
  const bookLinks = ['olid', 'bnf', 'gbooks'].filter((scheme) => ids[scheme]);
  // A game's IGDB page (by its slug) and IGDB's credit (§4b.4: courtesy, as BGG's).
  const igdbUrl = preset === 'videogame' ? igdbPage(values) : null;
  // A field named only by its key ("id_tmdb") reads as "TMDB ID".
  const label = (field) => (rawName(field) && sourceOf(field) ? t(`item.sourceName.${sourceOf(field)}`) : field.name);
  // Updated from the first of the model's sources whose id the item holds (TMDB's for films; a book's three).
  const hasSourceId = presetSources(preset, kinds).some((source) => !!ids[SOURCE_SCHEMES[source]]);
  const canRefresh = canEdit && hasSourceId;
  const canFind = canEdit && !hasSourceId && !!itemCode && presetSources(preset, kinds).length > 0;
  const canRelink = canEdit && hasSourceId;
  // A film's words name TMDB; a book's, its sources.
  const refreshKeys = presetSources(preset, kinds).includes('tmdb') ? 'itemForm.refresh' : 'itemForm.refreshSources';
  const editPath = `${basePath}/items/${item.id}/edit`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t('item.linkCopied'));
    } catch (error) {
      toast.error(t('item.linkNotCopied'));
    }
  };
  const refresh = async () => {
    setRefreshing(true);
    try {
      const response = await api.post(`/inventories/${inventory.id}/items/${item.id}/refresh-from-source`, {});
      const changed = (response.data.changed || []).map((key) => (fields.find((f) => f.key === key) || {}).name || key);
      toast[changed.length ? 'success' : 'info'](changed.length
        ? t(`${refreshKeys}.changed`, { fields: changed.join(', ') })
        : t(`${refreshKeys}.nothing`));
      setReload((n) => n + 1);
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(refreshError(t, error));
    } finally {
      setRefreshing(false);
    }
  };
  const remove = async () => {
    if (await onDelete(item)) navigate(listQuery(true), { replace: true });
  };

  const menuItems = [
    { id: 'copy', label: t('item.copyLink'), icon: LinkIcon, onSelect: copyLink },
    canRefresh && { id: 'refresh', label: refreshing ? t('itemForm.refresh.running') : t(`${refreshKeys}.button`), icon: RefreshIcon, onSelect: refresh, disabled: refreshing },
    canFind && { id: 'find', label: findDetails.busy ? t('itemForm.refresh.running') : t('findDetails.button'), icon: SearchIcon, onSelect: findDetails.refresh, disabled: findDetails.busy },
    canEdit && 'separator',
    canEdit && { id: 'delete', label: t('collection.deleteItem'), icon: TrashIcon, danger: true, onSelect: remove },
  ].filter(Boolean);

  const position = neighbours && neighbours.position;
  // Previous / next replace the address: Back returns to the list, not through every item seen.
  const pagerLink = (id, icon, label) => (id
    ? <IconButton as={Link} to={itemPath(id)} replace state={location.state} icon={icon} label={label} />
    : <IconButton icon={icon} label={label} disabled />);
  const number = (n) => new Intl.NumberFormat(lang).format(n);
  const lineParts = [
    summary.subtitle && subtitleField ? <em key="sub">{summary.subtitle}</em> : null,
    summary.year || null,
    measureField ? <FieldValue key="measure" field={measureField} value={values[fieldKey(measureField)]} /> : null,
  ].filter(Boolean);

  return (
    <article className="item-page" aria-labelledby="item-title">
      <div className="item-page__top">
        <Breadcrumb items={crumbs(title)} />
        {position && (
          <nav aria-label={t('item.position.label')} className="item-page__pager">
            {pagerLink(neighbours.previous_id, ChevronLeftIcon, t('item.position.previous'))}
            <span className="item-page__position">{t('item.position.of', { position: number(position), total: number(neighbours.total) })}</span>
            {pagerLink(neighbours.next_id, ChevronRightIcon, t('item.position.next'))}
          </nav>
        )}
      </div>

      <div className="item-page__body">
        <div className="item-page__media">
          {images.length > 0 && !coverFailed ? (
            <button type="button" className="item-page__cover" onClick={() => setLightbox(0)}
                    aria-label={t('item.openImage', { title })}>
              <img src={mediaUrl(images[0].id, 'medium')} alt="" onError={() => setCoverFailed(true)} />
            </button>
          ) : (
            <div className="item-page__cover item-page__cover--empty">
              <ItemCover mediaId={null} title={title} icon={Icon} />
            </div>
          )}
          {(images.length > 1 || (canEdit && imageFields.length > 0)) && (
            <ul className="item-page__thumbs">
              {images.slice(1).map((image, index) => (
                <li key={image.id}>
                  <button type="button" onClick={() => setLightbox(index + 1)}
                          aria-label={t('item.openImageOf', { caption: image.caption, n: index + 2, total: images.length })}>
                    <img src={mediaUrl(image.id, 'thumb')} alt="" loading="lazy" />
                  </button>
                </li>
              ))}
              {canEdit && imageFields.length > 0 && (
                <li>
                  <Link to={editPath} state={{ fromCollection: true }} className="item-page__add-photo" aria-label={t('item.addPhoto')}>
                    <PlusIcon size={20} />
                  </Link>
                </li>
              )}
            </ul>
          )}
        </div>

        <div className="item-page__main">
          <header className="item-page__header">
            <div className="item-page__heading">
              {(itemTypes.length > 1 || badgeField) && (
                <p className="item-page__badges">
                  {itemTypes.length > 1 && type && <Badge tone="ink">{type.name}</Badge>}
                  {badgeField && <Badge tone="outline">{String(selectLabel(badgeField, values[fieldKey(badgeField)], uiLang(i18n)))}</Badge>}
                </p>
              )}
              <h1 id="item-title" className="item-page__title">{title}</h1>
              {lineParts.length > 0 && (
                <p className="item-page__line">
                  {lineParts.map((part, i) => <React.Fragment key={i}>{i > 0 && ' · '}{part}</React.Fragment>)}
                </p>
              )}
              {creatorsField && summary.creators.length > 0 && (
                <p className="item-page__creators">
                  {creatorsField.name}{t('ui.colon')}{' '}
                  {creatorsField.type === 'tags' ? summary.creators.map((name, i) => (
                    <React.Fragment key={`${name}-${i}`}>
                      {i > 0 && ', '}
                      <Link className="item-page__creator" to={filterPath(basePath, `f.${fieldKey(creatorsField)}`, tagKey(name))}
                            title={t('item.filterBy', { name: creatorsField.name, value: name })}>
                        <strong>{name}</strong>
                      </Link>
                    </React.Fragment>
                  )) : <strong>{summary.creators.join(', ')}</strong>}
                </p>
              )}
              {canRelink && (
                <Button variant="ghost" size="sm" icon={SearchIcon} className="item-page__relink" onClick={relink.open} loading={relink.busy}>
                  {relink.label}
                </Button>
              )}
            </div>
            <div className="item-page__actions">
              {canEdit && (
                <Button as={Link} to={editPath} state={{ fromCollection: true }} variant="secondary" icon={EditIcon}>{t('common.edit')}</Button>
              )}
              {canFind && (
                <Button variant="primary" icon={SearchIcon} onClick={findDetails.refresh} loading={findDetails.busy}>{t('findDetails.button')}</Button>
              )}
              <Menu label={t('item.moreActions')} align="end" items={menuItems}
                    trigger={(props) => <IconButton {...props} icon={MoreIcon} label={t('item.moreActions')} />} />
            </div>
          </header>

          <MyOpinion key={item.id} inventory={inventory} item={item} itemType={type}
                     onSaved={(answer) => { if (onOpinionSaved) onOpinionSaved(item.id, answer); }} />

          {chipFields.length > 0 && (
            <ul className="item-page__chips" aria-label={chipFields.map((f) => f.name).join(', ')}>
              {chipFields.map((field) => <ValueChips key={fieldKey(field)} field={field} value={values[fieldKey(field)]} basePath={basePath} />)}
            </ul>
          )}

          {descriptionField && has(descriptionField) && <Description text={String(values[fieldKey(descriptionField)])} />}

          {(details.length > 0 || place) && (
            <section aria-labelledby="item-details" className="item-page__section">
              <h2 id="item-details" className="item-page__h2">{hasSources(preset, kinds) ? t('item.myCopy') : t('item.details')}</h2>
              <dl className="item-page__details">
                {details.map((field) => (
                  <div key={fieldKey(field)} className="item-page__fact">
                    <dt>{label(field)}</dt>
                    <dd><FieldDisplay field={field} value={values[fieldKey(field)]} tmdbKind={tmdbKind} /></dd>
                  </div>
                ))}
                {place && (
                  <div className="item-page__fact">
                    <dt>{t('collection.location')}</dt>
                    <dd>
                      {place.map((segment, i) => (
                        <React.Fragment key={segment.id}>
                          {i > 0 && ' › '}
                          <Link className="item-page__place-link" to={filterPath(basePath, 'place', String(segment.id))}
                                title={t('item.filterBy', { name: t('collection.filters.place'), value: placeName(t, segment) })}>
                            <bdi>{placeName(t, segment)}</bdi>
                          </Link>
                        </React.Fragment>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </section>
          )}

          {longLists.map((field) => <LongList key={fieldKey(field)} field={field} value={values[fieldKey(field)]} />)}

          <footer className="item-page__footer">
            <p>
              {item.created_by_name
                ? t('item.history.addedBy', { name: item.created_by_name, date: dayOf(item.created_at, lang) })
                : t('item.history.added', { date: dayOf(item.created_at, lang) })}
              {item.source_media_id && ` ${t('item.history.fromPhoto')}`}
            </p>
            {changedLater(item) && (
              <p>
                {item.updated_by_name
                  ? t('item.history.changedBy', { name: item.updated_by_name, date: dayOf(item.updated_at, lang) })
                  : t('item.history.changed', { date: dayOf(item.updated_at, lang) })}
              </p>
            )}
            {bookLinks.length > 0 && (
              <p className="item-page__sources">
                {bookLinks.map((scheme) => (
                  <a key={scheme} href={sourceUrl(scheme, ids[scheme])} target="_blank" rel="noopener noreferrer">
                    {t(`item.history.${scheme}Page`)} <span aria-hidden="true">↗</span>
                    <span className="visually-hidden"> {t('item.newTab')}</span>
                  </a>
                ))}
              </p>
            )}
            {(igdbUrl || ids.igdb) && (
              <p className="item-page__sources">
                {igdbUrl && (
                  <a href={igdbUrl} target="_blank" rel="noopener noreferrer">
                    {t('item.history.igdbPage')} <span aria-hidden="true">↗</span>
                    <span className="visually-hidden"> {t('item.newTab')}</span>
                  </a>
                )}
                {' '}<span className="item-page__credit">
                  <a href="https://www.igdb.com" target="_blank" rel="noopener noreferrer">{t('item.history.igdbCredit')}</a>
                </span>
              </p>
            )}
            {/* The BnF's Licence ouverte asks for the mention of the source where its data appears. */}
            {ids.bnf && <p className="item-page__credit">{t('item.history.bnfCredit')}</p>}
            {tmdbId || imdbId ? (
              <p className="item-page__sources">
                {tmdbId && (
                  <a href={sourceUrl('tmdb', tmdbId, tmdbKind)} target="_blank" rel="noopener noreferrer">
                    {t('item.history.tmdbPage')} <span aria-hidden="true">↗</span>
                    <span className="visually-hidden"> {t('item.newTab')}</span>
                  </a>
                )}
                {imdbId && (
                  <a href={sourceUrl('imdb', imdbId)} target="_blank" rel="noopener noreferrer">
                    {t('item.history.imdbPage')} <span aria-hidden="true">↗</span>
                    <span className="visually-hidden"> {t('item.newTab')}</span>
                  </a>
                )}
              </p>
            ) : null}
          </footer>
        </div>
      </div>

      {findDetails.dialog}
      {relink.dialog}
      {lightbox !== null && (
        <Lightbox images={images} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} title={title} />
      )}
    </article>
  );
}

export default ItemPage;
